/**
 * Non-blocking Places → backyard asset preload.
 * Warms HTTP cache for the correct presentation plate (+ optional shared props).
 */

import {
  backyardMasterFor,
  getBackyardPresentationMode,
  raleighMasterFor,
  spanishForkMasterFor,
  type BackyardPresentationMode,
} from '@/world-prototype/camera/backyardPresentation';
import { preloadImageUrls } from '@/world-prototype/perf/travelLoadProfiler';
import { resolveRuntimeArtUrl } from '@/world-prototype/perf/runtimeArtUrl';

const SHARED_PROP_BASE = `${import.meta.env.BASE_URL}art/environments/shared/props/`;
const SHARED_GROUND_BASE = `${import.meta.env.BASE_URL}art/environments/shared/ground/`;
const HUBERT_PROP_BASE = `${import.meta.env.BASE_URL}art/environments/jacksonville/backyard/props/`;
const HUBERT_GROUND_BASE = `${import.meta.env.BASE_URL}art/environments/jacksonville/backyard/ground/`;
const SCATTER_BASE = `${import.meta.env.BASE_URL}art/environments/shared/scatter/`;

/** High-value shared / frequent props (optional idle preload). */
export const SHARED_PRELOAD_PROP_URLS: string[] = [
  `${SHARED_PROP_BASE}gas-grill-v1.png`,
  `${SHARED_PROP_BASE}swingset-slide-v1.png`,
  `${SHARED_PROP_BASE}electric-pizza-oven-v1.png`,
  `${SHARED_PROP_BASE}porch-roof-under-v1.png`,
  `${SHARED_PROP_BASE}patio-roof-v1.png`,
  `${SHARED_PROP_BASE}gazebo-v1.png`,
  `${SHARED_PROP_BASE}small-bridge-v1.png`,
  `${SHARED_PROP_BASE}bush-v1.png`,
  `${SHARED_PROP_BASE}bush-v2.png`,
  `${SHARED_GROUND_BASE}gravel-pad.png`,
  `${SHARED_GROUND_BASE}circle-patio-v1.png`,
  `${SHARED_GROUND_BASE}rounded-concrete-patio-v1.png`,
  `${HUBERT_GROUND_BASE}patio-v1.png`,
  `${SCATTER_BASE}pinecones-v1.png`,
];

/** Hubert-only props worth warming after masters. */
export const HUBERT_PRELOAD_PROP_URLS: string[] = [
  `${HUBERT_PROP_BASE}storage-shed-v2.png`,
  `${HUBERT_PROP_BASE}tree-left-v1.png`,
  `${HUBERT_PROP_BASE}tree-right-v1.png`,
  `${HUBERT_PROP_BASE}garden-bed-v1.png`,
  `${HUBERT_PROP_BASE}garden-bed-flowers-v1.png`,
  `${HUBERT_PROP_BASE}fire-pit-v1.png`,
  `${HUBERT_PROP_BASE}clothesline-v1.png`,
  `${HUBERT_PROP_BASE}hammock-v1.png`,
  `${HUBERT_PROP_BASE}little-pine-tree-v1.png`,
  `${SHARED_PROP_BASE}bucket-v1.png`,
];

let startedForMode: BackyardPresentationMode | null = null;
let idleHandle: number | null = null;

function runtimeUrls(urls: string[]): string[] {
  return urls.map((u) => resolveRuntimeArtUrl(u));
}

/** Background plates for all yards at the current device plate. */
export function backyardBackgroundUrlsFor(
  mode: BackyardPresentationMode,
): string[] {
  return runtimeUrls([
    backyardMasterFor(mode).src,
    raleighMasterFor(mode).src,
    spanishForkMasterFor(mode).src,
  ]);
}

/**
 * Start warming backyard backgrounds once Places is interactive.
 * Non-blocking; cancels/restarts if presentation mode changes.
 */
export function startPlacesBackyardPreload(
  playW: number,
  playH: number,
  options: { includeProps?: boolean } = {},
): void {
  if (typeof window === 'undefined') return;
  const mode = getBackyardPresentationMode(playW, playH);
  if (startedForMode === mode) return;
  startedForMode = mode;

  const backgrounds = backyardBackgroundUrlsFor(mode);
  // Masters first — highest priority for perceived travel.
  void preloadImageUrls(backgrounds);

  if (!options.includeProps) return;

  const schedule =
    typeof window.requestIdleCallback === 'function'
      ? (cb: () => void) => {
          idleHandle = window.requestIdleCallback(() => cb(), { timeout: 2500 });
        }
      : (cb: () => void) => {
          idleHandle = window.setTimeout(cb, 400) as unknown as number;
        };

  schedule(() => {
    void preloadImageUrls(
      runtimeUrls([...SHARED_PRELOAD_PROP_URLS, ...HUBERT_PRELOAD_PROP_URLS]),
    );
  });
}

export function stopPlacesBackyardPreload(): void {
  if (idleHandle != null) {
    if (typeof window.cancelIdleCallback === 'function') {
      window.cancelIdleCallback(idleHandle);
    } else {
      window.clearTimeout(idleHandle);
    }
    idleHandle = null;
  }
}

/** Reset so a later Places visit can preload again (e.g. after plate change). */
export function resetPlacesBackyardPreload(): void {
  stopPlacesBackyardPreload();
  startedForMode = null;
}
